/*
 * Rechtstreeks naar de brug die Android zelf in het venster zet.
 *
 * WAAROM NIET registerPlugin() UIT @capacitor/core. Dat is de nette manier, en
 * hij werkte hier niet: de aanroep ging eruit en er kwam nooit antwoord terug.
 * Geen fout, geen afwijzing, alleen stilte - en daar helpt geen try/catch tegen.
 *
 * De verklaring is dat `import("@capacitor/core")` een eigen brok oplevert, en
 * daarmee een tweede exemplaar van de brug naast het exemplaar dat de andere
 * plugins gebruiken. De aanroep vertrekt dan bij de ene en het antwoord wordt
 * bij de andere bezorgd, waar niemand er meer op wacht. Dat het alleen mijn twee
 * eigen plugins trof en @capacitor/push-notifications niet, past daar precies
 * bij: die zat al in het brok dat als eerste werd geladen.
 *
 * `window.Capacitor` is er maar één. Dat is het exemplaar dat Android zelf
 * inschiet voordat onze javascript draait, en dat lib/capacitor.ts al gebruikt
 * om te bepalen of we in de app draaien. Daar hangen we hier ook aan.
 */

type Methode = (...argumenten: unknown[]) => Promise<unknown>;

type Brug = {
  Plugins?: Record<string, Record<string, Methode> | undefined>;
  PluginHeaders?: Array<{ name: string }>;
};

function brug(): Brug | null {
  if (typeof window === "undefined") return null;
  return (window as unknown as { Capacitor?: Brug }).Capacitor ?? null;
}

/** De plugins die de brug kent. Puur om te kunnen zien wat er wél is als iets
 *  ontbreekt - zonder die lijst is "werkt niet" niet te duiden, en een telefoon
 *  zonder usb-kabel geeft je verder niets. */
export function pluginNamen(): string[] {
  const b = brug();
  const uitHeaders = (b?.PluginHeaders ?? []).map((h) => h.name);
  const uitPlugins = Object.keys(b?.Plugins ?? {});
  return Array.from(new Set([...uitHeaders, ...uitPlugins])).sort();
}

/** Een plugin bij naam, of een fout die zegt wat er dan wél is. */
export function nativePlugin<T>(naam: string): T {
  const p = brug()?.Plugins?.[naam];
  if (!p) {
    const bekend = pluginNamen();
    throw new Error(
      `${naam} niet gevonden bij de brug (wel: ${bekend.length ? bekend.join(", ") : "geen"})`,
    );
  }
  return p as T;
}
